import { gql } from '@apollo/client';

export const CREATE_INITIAL_ADMIN = gql`
  mutation CreateInitialAdmin($input: CreateInitialAdminInput!) {
    createInitialAdmin(input: $input) {
      id
      email
      role
      firstName
      lastName
      isActive
    }
  }
`;

export const LOGIN = gql`
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      token
      refreshToken
      expiresIn
      refreshExpiresIn
      user {
        id
        email
        firstName
        lastName
        role
        isActive
        isFirstLogin
        phoneNumber
        avatarUrl
      }
    }
  }
`;

export const REGISTER_USER = gql`
  mutation Register($input: CreateUserInput!) {
    register(input: $input) {
      id
      email
      firstName
      lastName
      role
      isActive
      name
    }
  }
`;

export const ASSIGN_JOBS = gql`
  mutation AssignJobs($jobIds: [ID!]!, $userId: ID!) {
    assignJobs(jobIds: $jobIds, userId: $userId) {
      id
      status
      category
      scheduledDate
      notes
      property {
        id
        name
        address
        propertyCode
      }
      reservation {
        id
        guest_name
        check_in
        check_out
      }
      assignedTo {
        id
        name
        role
      }
      assignedTeamId
      createdAt
      updatedAt
    }
  }
`;

export const ASSIGN_JOBS_TO_TEAM = gql`
  mutation AssignJobsToTeam($jobIds: [ID!]!, $teamId: Int!) {
    assignJobsToTeam(jobIds: $jobIds, teamId: $teamId) {
      id
      status
      category
      scheduledDate
      notes
      property {
        id
        name
        address
        propertyCode
      }
      reservation {
        id
        guest_name
        check_in
        check_out
      }
      assignedTo {
        id
        name
        role
      }
      assignedTeamId
      assignedTeam {
        id
        name
        color
        memberCount
      }
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_JOB = gql`
  mutation UpdateJob($id: ID!, $input: UpdateJobInput!) {
    updateJob(id: $id, input: $input) {
      id
      status
      category
      scheduledDate
      startTime
      notes
      hasDateOverride
      hasTimeOverride
      hasServiceTypeOverride
      flipOverridden
      flipOverrideValue
      isFlip
      payout
      billedAmount
      serviceType {
        id
        title
        category
        color
      }
      property {
        id
        name
        address
        propertyCode
      }
      reservation {
        id
        guest_name
        check_in
        check_out
      }
      assignedTo {
        id
        firstName
        lastName
        email
        name
        role
      }
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_USER_ROLE = gql`
  mutation UpdateUserRole($userId: Float!, $role: String!) {
    updateUserRole(userId: $userId, role: $role) {
      id
      email
      firstName
      lastName
      role
      isActive
      locationPreference
    }
  }
`;

export const ADMIN_UPDATE_USER = gql`
  mutation AdminUpdateUser($userId: Float!, $input: UpdateProfileInput!) {
    updateProfile(userId: $userId, input: $input) {
      id
      firstName
      lastName
      email
      phoneNumber
      role
      isActive
      locationPreference
      areas
      name
      avatarUrl
      smsNotifications
      emailNotifications
      notificationEmailOverride
    }
  }
`;

export const UPDATE_USER_PROFILE = gql`
  mutation UpdateUserProfile($userId: Float!, $input: UpdateProfileInput!) {
    updateProfile(userId: $userId, input: $input) {
      id
      firstName
      lastName
      email
      phoneNumber
      role
      isActive
      locationPreference
      areas
      name
      avatarUrl
      smsNotifications
      emailNotifications
      notificationEmailOverride
    }
  }
`;

export const UPDATE_USER_PASSWORD = gql`
  mutation UpdateUserPassword($userId: Float!, $password: String!) {
    updatePassword(userId: $userId, password: $password) {
      success
      message
    }
  }
`;

export const UPDATE_RESERVATION = gql`
  mutation UpdateReservation($input: UpdateReservationInput!) {
    updateReservation(input: $input) {
      id
      check_in
      check_out
      check_in_time
      check_out_time
      override_check_in
      override_check_out
      has_time_override
      guest_name
      email
      mobile_phone
      is_cancelled
      status_code
      status_id
      property {
        id
        name
        address
        city
        state
        zip
      }
    }
  }
`;

export const UPDATE_JOB_STATUS = gql`
  mutation UpdateJobStatus($id: ID!, $status: JobStatus!, $notes: String, $gps: JobStatusGPSInput) {
    updateJobStatus(id: $id, status: $status, notes: $notes, gps: $gps) {
      id
      status
      notes
      updatedAt
      statusHistory {
        id
        status
        timestamp
        notes
        latitude
        longitude
        accuracy
        updatedBy {
          id
          name
        }
      }
    }
  }
`;

export const BULK_COMPLETE_JOBS = gql`
  mutation BulkCompleteJobs($input: BulkCompleteJobsInput!) {
    bulkCompleteJobs(input: $input) {
      success
      successCount
      failureCount
      summary
      results {
        jobId
        success
        newStatus
        error
        jobCategory
      }
    }
  }
`;

export const SAVE_JOB_CHECKLIST = gql`
  mutation SaveJobChecklist($input: JobChecklistCompletionInput!) {
    saveJobChecklistCompletion(input: $input) {
      id
      status
      updatedAt
    }
  }
`;

export const SAVE_JOB_CHECKLIST_ITEM = gql`
  mutation SaveJobChecklistItem($input: JobChecklistItemSaveInput!) {
    saveJobChecklistItemCompletion(input: $input) {
      id
      itemId
      completed
    }
  }
`;

export const GENERATE_SHARE_TOKEN = gql`
  mutation GenerateShareToken($input: GenerateShareTokenInput!) {
    generateShareToken(input: $input) {
      token
      expiresAt
    }
  }
`;

export const UPDATE_PUBLIC_JOB_STATUS = gql`
  mutation UpdatePublicJobStatus($id: ID!, $status: String!, $token: String, $gps: JobStatusGPSInput) {
    updatePublicJobStatus(id: $id, status: $status, token: $token, gps: $gps) {
      id
      status
      updatedAt
      statusHistory {
        id
        status
        timestamp
        notes
        latitude
        longitude
        accuracy
      }
    }
  }
`;

export const MARK_CLEANING_COMPLETE = gql`
  mutation MarkCleaningComplete($id: ID!, $notes: String, $gps: JobStatusGPSInput) {
    markCleaningComplete(id: $id, notes: $notes, gps: $gps) {
      id
      status
      notes
      updatedAt
      statusHistory {
        id
        status
        timestamp
        notes
        latitude
        longitude
        accuracy
        updatedBy {
          id
          name
        }
      }
    }
  }
`;

export const MARK_PUBLIC_CLEANING_COMPLETE = gql`
  mutation MarkPublicCleaningComplete($id: ID!, $token: String, $notes: String) {
    markPublicCleaningComplete(id: $id, token: $token, notes: $notes) {
      id
      status
      updatedAt
    }
  }
`;

export const MARK_INSPECTION_COMPLETE = gql`
  mutation MarkInspectionComplete($id: ID!, $notes: String, $gps: JobStatusGPSInput) {
    markInspectionComplete(id: $id, notes: $notes, gps: $gps) {
      id
      status
      statusHistory {
        id
        status
        timestamp
        notes
        latitude
        longitude
        accuracy
        updatedBy {
          id
          name
        }
      }
    }
  }
`;

export const CREATE_WORK_ORDER = gql`
  mutation CreateWorkOrder($input: CreateWorkOrderInput!) {
    createWorkOrder(input: $input) {
      id
      description
      isUrgent
      isAfterHours
      isFeedback
      workOrderType
      status
      createdAt
      updatedAt
      photos
      videos {
        id
        url
        thumbnailUrl
        duration
        sizeBytes
        createdAt
        expiresAt
      }
      propertyId
      assignedToId
      assignedTeamId
      assignedTeam {
        id
        name
        color
      }
      assignedRoles
      guestAlertsEnabled
      guestAlertDescription
      guestAlertRecipientName
      guestAlertContactSource
      guestAssignmentAlertProcessed
      property {
        id
        name
        address
        city
        state
        zip
        area
        propertyCode
      }
      reservationId
      reservation {
        id
        guest_name
        check_in
        check_out
      }
      submittedBy {
        id
        name
      }
      assignedTo {
        id
        name
        role
      }
      dueDate
    }
  }
`;

export const UPDATE_WORK_ORDER = gql`
  mutation UpdateWorkOrder($id: ID!, $input: UpdateWorkOrderInput!) {
    updateWorkOrder(id: $id, input: $input) {
      id
      description
      isUrgent
      isAfterHours
      workOrderType
      status
      updatedAt
      photos
      videos {
        id
        url
        thumbnailUrl
        duration
        sizeBytes
        createdAt
        expiresAt
      }
      dueDate
      isExcludedFromInvoicing
      isLaborUnbillable
      isServiceChargeEnabled
      serviceChargeAmount
      editedTotalTimeMinutes
      guestAlertsEnabled
      guestAlertsDisabledAt
    }
  }
`;

export const REQUEST_WORK_ORDER_APPROVAL = gql`
  mutation RequestWorkOrderApproval($id: ID!, $reason: String!) {
    requestWorkOrderApproval(id: $id, reason: $reason) {
      id
      needsApproval
      approvalReason
      approvalRequestedAt
      status
    }
  }
`;

export const APPROVE_WORK_ORDER = gql`
  mutation ApproveWorkOrder($id: ID!) {
    approveWorkOrder(id: $id) {
      id
      needsApproval
      approvalApproved
      approvalApprovedAt
      approvedBy {
        id
        name
      }
      status
    }
  }
`;

export const CREATE_WORK_ORDER_COMMENT = gql`
  mutation CreateWorkOrderComment($input: CreateCommentInput!) {
    createWorkOrderComment(input: $input) {
      id
      workOrderId
      userId
      comment
      isEscalation
      attachments
      createdAt
      updatedAt
      user {
        id
        name
        firstName
        lastName
        role
      }
    }
  }
`;

export const UPDATE_WORK_ORDER_COMMENT = gql`
  mutation UpdateWorkOrderComment($id: ID!, $input: UpdateCommentInput!) {
    updateWorkOrderComment(id: $id, input: $input) {
      id
      comment
      editedAt
      updatedAt
    }
  }
`;

export const DELETE_WORK_ORDER_COMMENT = gql`
  mutation DeleteWorkOrderComment($id: ID!) {
    deleteWorkOrderComment(id: $id)
  }
`;

export const MARK_WORK_ORDER_COMMENTS_READ = gql`
  mutation MarkWorkOrderCommentsRead($workOrderId: ID!) {
    markWorkOrderCommentsAsRead(workOrderId: $workOrderId)
  }
`;

export const ASSIGN_WORK_ORDER = gql`
  mutation AssignWorkOrder($input: AssignWorkOrderInput!) {
    assignWorkOrder(input: $input) {
      id
      status
      assignedTo {
        id
        name
        role
      }
      assignedTeamId
      assignedTeam {
        id
        name
        color
      }
      assignedRoles
      guestAlertsEnabled
      guestAssignmentAlertProcessed
      updatedAt
    }
  }
`;

export const DELETE_WORK_ORDER = gql`
  mutation DeleteWorkOrder($id: ID!) {
    deleteWorkOrder(id: $id)
  }
`;

export const RESTORE_WORK_ORDER = gql`
  mutation RestoreWorkOrder($id: ID!) {
    restoreWorkOrder(id: $id) {
      id
      status
      isDeleted
      deletedAt
      updatedAt
    }
  }
`;

export const UPDATE_WORK_ORDER_STATUS = gql`
  mutation UpdateWorkOrderStatus($id: ID!, $status: WorkOrderStatus!, $acceptedById: ID) {
    updateWorkOrderStatus(id: $id, status: $status, acceptedById: $acceptedById) {
      id
      status
      updatedAt
      assignedToId
      assignedRoles
      guestAlertsEnabled
      guestAssignmentAlertProcessed
      assignedTo {
        id
        name
        role
      }
      needsApproval
      approvalApproved
      submittedBy {
        id
        name
      }
    }
  }
`;

export const CREATE_PROPERTY = gql`
  mutation CreateProperty($input: CreatePropertyInput!) {
    createProperty(input: $input) {
      id
      streamlinePropertyId
      name
      address
      city
      state
      zip
      area
      propertyCode
      status
      isActive
      lastSynced
      checkInTime
      checkOutTime
      entryDetails
      propertyManagerId
      propertyManagerIds
      propertyManagementTeamId
      propertyManagementTeam {
        id
        name
        color
        department
      }
      beds
      baths
      housekeepingNotes
      stagingNotes
      stagingPhotos
      propertyManagerNotes
      websiteLink
      price
      payout
      externalId
      integratedWithStreamline
      created_at
      updated_at
    }
  }
`;

export const UPDATE_OWN_PROFILE = gql`
  mutation UpdateOwnProfile($input: UpdateProfileInput!) {
    updateOwnProfile(input: $input) {
      id
      firstName
      lastName
      phoneNumber
      email
      name
      role
      avatarUrl
      smsNotifications
      emailNotifications
      notificationEmailOverride
      isActive
      locationPreference
      areas
    }
  }
`;

export const UPDATE_OWN_PASSWORD = gql`
  mutation UpdateOwnPassword($currentPassword: String!, $newPassword: String!) {
    updateOwnPassword(currentPassword: $currentPassword, newPassword: $newPassword)
  }
`;

export const ACTIVATE_USER = gql`
  mutation ActivateUser($userId: Float!) {
    activateUser(userId: $userId)
  }
`;

export const DEACTIVATE_USER = gql`
  mutation DeactivateUser($userId: Float!) {
    deactivateUser(userId: $userId)
  }
`;

export const CREATE_NOTE = gql`
  mutation CreateNote($input: CreateNoteInput!) {
    createNote(input: $input) {
      id
      title
      body
      createdAt
      startDate
      endDate
      visibleToRoles
      visibleToAreas
      author {
        id
        name
      }
    }
  }
`;

export const UPDATE_NOTE = gql`
  mutation UpdateNote($id: ID!, $input: CreateNoteInput!) {
    updateNote(id: $id, input: $input) {
      id
      title
      body
      createdAt
      startDate
      endDate
      visibleToRoles
      visibleToAreas
      author {
        id
        name
      }
    }
  }
`;

export const DELETE_NOTE = gql`
  mutation DeleteNote($id: ID!) {
    deleteNote(id: $id) {
      success
      message
    }
  }
`;

export const DELETE_JOB = gql`
  mutation DeleteJob($id: ID!) {
    removeJob(id: $id)
  }
`;

export const RESTORE_JOB = gql`
  mutation RestoreJob($id: ID!) {
    restoreJob(id: $id)
  }
`;

export const MANUALLY_CANCEL_JOB = gql`
  mutation ManuallyCancelJob($id: ID!, $reason: String) {
    manuallyCancelJob(id: $id, reason: $reason) {
      id
      status
      notes
      updatedAt
    }
  }
`;

export const REQUEST_PASSWORD_RESET = gql`
  mutation RequestPasswordReset($input: RequestPasswordResetInput!) {
    requestPasswordReset(input: $input)
  }
`;

export const RESET_PASSWORD = gql`
  mutation ResetPassword($input: ResetPasswordInput!) {
    resetPassword(input: $input)
  }
`;

export const UPDATE_PASSWORD_FIRST_TIME = gql`
  mutation UpdatePasswordFirstTime($password: String!) {
    updatePasswordFirstTime(password: $password)
  }
`;

export const RESET_FIRST_LOGIN = gql`
  mutation ResetFirstLogin {
    resetFirstLogin
  }
`;

export const REFRESH_TOKEN = gql`
  mutation RefreshToken {
    refreshToken {
      token
      refreshToken
      expiresIn
      refreshExpiresIn
      user {
        id
        email
        firstName
        lastName
        role
        isActive
        isFirstLogin
        phoneNumber
        avatarUrl
      }
    }
  }
`;

export const LOGOUT = gql`
  mutation Logout {
    logout
  }
`;

export const UPDATE_JOB_SCHEDULED_DATE = gql`
  mutation UpdateJobScheduledDate($id: ID!, $scheduledDate: DateTime!, $hasDateOverride: Boolean) {
    updateJobScheduledDate(id: $id, scheduledDate: $scheduledDate, hasDateOverride: $hasDateOverride) {
      id
      scheduledDate
      hasDateOverride
      updatedAt
    }
  }
`;

export const BULK_UPDATE_JOB_DATES = gql`
  mutation BulkUpdateJobDates($input: BulkUpdateJobDatesInput!) {
    bulkUpdateJobDates(input: $input) {
      success
      updatedCount
      errors
      updatedJobs {
        id
        scheduledDate
        hasDateOverride
        updatedAt
        property {
          id
          name
          propertyCode
        }
      }
    }
  }
`;

export const BULK_UPDATE_AND_ASSIGN_JOBS = gql`
  mutation BulkUpdateAndAssignJobs($input: BulkUpdateAndAssignJobsInput!) {
    bulkUpdateAndAssignJobs(input: $input) {
      success
      updatedCount
      assignedCount
      errors
      summary
      processedJobs {
        id
        scheduledDate
        hasDateOverride
        updatedAt
        assignedTo {
          id
          name
        }
        assignedTeam {
          id
          name
        }
        property {
          id
          name
          propertyCode
        }
      }
    }
  }
`;

export const GET_UPDATE_USER_AREAS_INPUT_FIELDS = gql`
  query GetUpdateUserAreasInputFields {
    __type(name: "UpdateUserAreasInput") {
      name
      kind
      inputFields {
        name
        type {
          name
          kind
          ofType {
            name
            kind
          }
        }
      }
    }
  }
`;

export const UPDATE_USER_AREAS = gql`
  mutation UpdateUserAreas(
    $input: UpdateUserAreasInput!
  ) {
    updateUserAreas(
      input: $input
    ) {
      id
      areas
    }
  }
`;

export const GET_USER_BY_ID = gql`
  query GetUserById($id: Float!) {
    user(id: $id) {
      id
      firstName
      lastName
      email
      role
      isActive
      areas
    }
  }
`;

export const CANCEL_JOB = gql`
  mutation CancelJob($id: ID!, $reason: String) {
    cancelJob(id: $id, reason: $reason) {
      id
      status
      category
      scheduledDate
      property {
        id
        name
      }
      assignedTo {
        id
        name
      }
      updatedAt
    }
  }
`;

export const SEND_EMAIL_ALERT = gql`
  mutation SendEmailAlert($jobId: ID!) {
    sendEmailAlert(jobId: $jobId)
  }
`;

export const SEND_TEXT_ALERT = gql`
  mutation SendTextAlert($jobId: ID!) {
    sendTextAlert(jobId: $jobId)
  }
`;
